import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';
import { formatCurrency, parseCurrency } from '../../utils/currency';
import { getGoalBalances } from '../../utils/activity';
import { useBalance } from '../../hooks/useBalance';
import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import '../../styles/brand-typography.css';
import './Savings.css';

const MAX_ACTIVE_GOALS = 3;

const Savings = () => {
  const navigate = useNavigate();
  const isNativeKidApp = Capacitor.isNativePlatform() && import.meta.env.VITE_APP_AUDIENCE === 'kids';
  const {
    currentKid,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;
  const {
    balance,
    savingsBalance,
    loading: balanceLoading,
    error: balanceError,
    refresh: refreshBalance,
  } = useBalance(currentKid?.id, currentKid?.family_id, 'kid');

  const [goals, setGoals] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [depositGoal, setDepositGoal] = useState(null);
  const [transferGoal, setTransferGoal] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [newGoal, setNewGoal] = useState({ name: '', amount: '' });
  const [addAmount, setAddAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSavingsData = async () => {
    if (!currentKid?.id) return;

    try {
      setPageLoading(true);
      setError('');
      const [goalData, ledgerData] = await Promise.all([
        db.getSavingsGoals(currentKid.id),
        db.getSavingsLedger(currentKid.id),
      ]);
      setGoals(goalData || []);
      setLedger(ledgerData || []);
    } catch (err) {
      console.error('Error loading savings:', err);
      setError(err?.message || 'Unable to load your savings. Please try again.');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (currentKid?.id) {
      loadSavingsData();
    }
  }, [currentKid?.id]);

  const activeGoals = useMemo(
    () => goals.filter((goal) => goal.status === 'active'),
    [goals]
  );

  const goalBalances = useMemo(
    () => getGoalBalances(goals, ledger),
    [goals, ledger]
  );

  const refreshAll = async () => {
    await loadSavingsData();
    refreshBalance();
  };

  const getGoalBalance = (goalId) => Math.max(0, goalBalances.get(goalId) || 0);

  const getSavingsProgress = (goal) => {
    if (!goal?.target_amount_cents) return 0;
    return Math.min(
      (getGoalBalance(goal.id) / Number(goal.target_amount_cents)) * 100,
      100
    );
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    setLoading(true);
    setError('');

    try {
      const amountCents = parseCurrency(newGoal.amount);

      if (activeGoals.length >= MAX_ACTIVE_GOALS) {
        throw new Error('You already have 3 active goals. Complete or cancel one first.');
      }

      if (amountCents <= 0) {
        throw new Error('Goal amount must be greater than $0');
      }

      await db.createSavingsGoal({
        name: newGoal.name,
        target_amount_cents: amountCents,
      });

      setShowNewGoal(false);
      setNewGoal({ name: '', amount: '' });
      await refreshAll();
    } catch (err) {
      setError(err?.message || 'Unable to create your savings goal.');
    } finally {
      setLoading(false);
    }
  };

  const openDeposit = (goal) => {
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }

    setError('');
    setTransferGoal(null);
    setTransferTargetId('');
    setTransferAmount('');
    setDepositGoal(goal);
    setAddAmount('');
  };

  const handleAddMoney = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    setLoading(true);
    setError('');

    try {
      const amountCents = parseCurrency(addAmount);
      const currentGoalBalance = getGoalBalance(depositGoal.id);
      const remaining = Math.max(
        Number(depositGoal.target_amount_cents) - currentGoalBalance,
        0
      );

      if (amountCents <= 0) {
        throw new Error('Amount must be greater than $0');
      }

      if (amountCents > balance) {
        throw new Error(
          `You only have ${formatCurrency(balance)} available in Pocket Money.`
        );
      }

      if (amountCents > remaining) {
        throw new Error(
          `You only need ${formatCurrency(remaining)} more to reach this goal.`
        );
      }

      await db.depositSavings(depositGoal.id, amountCents);

      setDepositGoal(null);
      setAddAmount('');
      await refreshAll();
    } catch (err) {
      setError(err?.message || 'Unable to add money to this goal.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGoal = async (goal) => {
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }

    const savedAmount = getGoalBalance(goal.id);
    const confirmed = window.confirm(
      `Cancel “${goal.name}”? ${formatCurrency(savedAmount)} will be returned to your Pocket Money.`
    );

    if (!confirmed) return;

    setLoading(true);
    setError('');
    try {
      await db.cancelSavingsGoal(goal.id);
      await refreshAll();
    } catch (err) {
      setError(err?.message || 'Unable to cancel this savings goal.');
    } finally {
      setLoading(false);
    }
  };

  const getEligibleTransferTargets = (sourceGoal) => {
    return activeGoals.filter((candidate) => {
      if (candidate.id === sourceGoal.id) return false;

      const availableRoom = Math.max(
        Number(candidate.target_amount_cents) - getGoalBalance(candidate.id),
        0
      );

      return availableRoom > 0;
    });
  };

  const openTransfer = (goal) => {
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }

    const firstTarget = getEligibleTransferTargets(goal)[0];
    setDepositGoal(null);
    setTransferGoal(goal);
    setTransferTargetId(firstTarget?.id || '');
    setTransferAmount('');
    setError('');
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    const target = activeGoals.find((goal) => goal.id === transferTargetId);

    if (!target || !transferGoal) return;

    const amountCents = parseCurrency(transferAmount);
    const sourceBalance = getGoalBalance(transferGoal.id);
    const targetRoom = Math.max(
      Number(target.target_amount_cents) - getGoalBalance(target.id),
      0
    );

    if (amountCents <= 0) {
      setError('Transfer amount must be greater than $0.');
      return;
    }

    if (amountCents > sourceBalance) {
      setError(`You only have ${formatCurrency(sourceBalance)} saved in ${transferGoal.name}.`);
      return;
    }

    if (amountCents > targetRoom) {
      setError(`${target.name} only has room for ${formatCurrency(targetRoom)} more.`);
      return;
    }

    const confirmed = window.confirm(
      `Transfer ${formatCurrency(amountCents)} from “${transferGoal.name}” to “${target.name}”?`
    );

    if (!confirmed) return;

    setLoading(true);
    setError('');
    try {
      await db.transferSavingsGoal(transferGoal.id, target.id, amountCents);
      setTransferGoal(null);
      setTransferTargetId('');
      setTransferAmount('');
      await refreshAll();
    } catch (err) {
      setError(err?.message || 'Unable to transfer this savings money.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteGoal = async (goal) => {
    const savedAmount = getGoalBalance(goal.id);
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    const confirmed = window.confirm(
      `Complete “${goal.name}”? ${formatCurrency(savedAmount)} will be used for this goal and removed from your savings.`
    );

    if (!confirmed) return;

    setLoading(true);
    setError('');
    try {
      await db.completeSavingsGoal(goal.id);
      await refreshAll();
    } catch (err) {
      setError(err?.message || 'Unable to complete this savings goal.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentKid) {
    navigate('/kid/dashboard');
    return null;
  }

  return (
    <KidLayout
      className="mypiggy-brand kid-savings"
      title="My Savings"
      avatar={currentKid.avatar}
      showBack
    >
      <div className={isNativeKidApp ? "space-y-4 pb-48" : "space-y-4"}>
        <Card className="p-4 bg-gradient-to-br from-blue-700 to-blue-800 text-white text-center">
          <h2 className="flex items-center justify-center gap-2 text-lg font-extrabold">
            <MyPiggyIcon name="my-savings" size="sm" />
            Total Savings
          </h2>
          <p className="mt-2 text-4xl font-extrabold leading-tight tabular-nums">
            {balanceLoading ? 'Loading...' : formatCurrency(savingsBalance)}
          </p>
          <div className="mt-3 rounded-lg border border-white/20 bg-blue-950/20 px-3 py-2">
            <p className="text-sm font-bold text-blue-100">Pocket Money Available to Save</p>
            <p className="mt-0.5 text-xl font-extrabold text-amber-100 tabular-nums">
              {balanceLoading ? 'Loading...' : formatCurrency(balance)}
            </p>
          </div>
        </Card>

        {(error || balanceError) && (
          <Card className="p-4 bg-red-50 border-red-200">
            <p className="text-sm text-red-700">
              {error || 'Unable to calculate your current balance.'}
            </p>
          </Card>
        )}

        <Card className="p-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-extrabold text-lg text-slate-950 whitespace-nowrap">
              <MyPiggyIcon name="savings-goal" size="md" className="savings-heading-icon" />
              My Goals
            </h2>
            <p className="text-xs sm:text-sm font-extrabold text-teal-900 text-right">
              {activeGoals.length} of {MAX_ACTIVE_GOALS} active goals
            </p>
          </div>
          {activeGoals.length < MAX_ACTIVE_GOALS && (
            <Button
              variant="kid"
              className="w-full mt-2"
              disabled={isWriteLocked}
              onClick={() => {
                setError('');
                setShowNewGoal(true);
              }}
            >
              + New Goal
            </Button>
          )}
        </Card>

        {showNewGoal && (
          <Card className="p-4">
            <h3 className="font-extrabold text-lg mb-4">Create New Savings Goal</h3>
            <form onSubmit={handleCreateGoal} className="space-y-4">
              <Input
                label="What are you saving for?"
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                placeholder="New bike, toy, game..."
                required
              />
              <Input
                label="How much do you need?"
                type="number"
                value={newGoal.amount}
                onChange={(e) => setNewGoal({ ...newGoal, amount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
              />
              <div className="flex space-x-3">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowNewGoal(false)}>
                  Back
                </Button>
                <Button type="submit" variant="kid" className="flex-1" disabled={loading || isWriteLocked}>
                  {loading ? 'Creating...' : 'Create Goal'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {pageLoading ? (
          <Card className="p-4 text-center">Loading your goals...</Card>
        ) : activeGoals.length === 0 ? (
          <Card className="p-4 text-center">
            <MyPiggyIcon name="create-new-savings-goal" size="hero" className="mb-4" />
            <h3 className="font-extrabold text-lg mb-2">No Savings Goals Yet</h3>
            <p className="text-gray-600 mb-6">
              You can create up to 3 goals for things you want to save for.
            </p>
          {!showNewGoal && (
            <Button
              variant="kid"
              disabled={isWriteLocked}
              onClick={() => setShowNewGoal(true)}
            >
              Create My First Goal
            </Button>
          )}
          </Card>
        ) : (
          <div className="space-y-4">
            {[...activeGoals].sort((a, b) => getSavingsProgress(b) - getSavingsProgress(a)).map((goal) => {
              const savedAmount = getGoalBalance(goal.id);
              const progress = getSavingsProgress(goal);
              const fullyFunded = savedAmount >= Number(goal.target_amount_cents);
              const eligibleTransferTargets = getEligibleTransferTargets(goal);
              const isDepositOpen = depositGoal?.id === goal.id;
              const isTransferOpen = transferGoal?.id === goal.id;
              const selectedTransferTarget = activeGoals.find(
                (candidate) => candidate.id === transferTargetId
              );
              const selectedTargetRoom = selectedTransferTarget
                ? Math.max(
                    Number(selectedTransferTarget.target_amount_cents) -
                      getGoalBalance(selectedTransferTarget.id),
                    0
                  )
                : 0;

              return (
                <React.Fragment key={goal.id}>
                  <Card className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <MyPiggyIcon name="savings-goal" size="sm" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-extrabold text-lg leading-snug break-words text-slate-950">{goal.name}</h3>
                        <p className="text-base font-extrabold text-teal-800 mt-1">
                          {formatCurrency(savedAmount)} of {formatCurrency(goal.target_amount_cents)}
                        </p>
                      </div>
                      <span className="font-extrabold text-lg text-purple-800">{Math.round(progress)}%</span>
                    </div>

                    <div role="progressbar" aria-label={`${goal.name} savings progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                      <div
                        className="bg-gradient-to-r from-purple-400 to-pink-500 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    {fullyFunded && (
                      <div className="rounded-xl bg-green-50 border border-green-200 p-3 mb-4 text-center">
                        <p className="flex items-center justify-center gap-2 font-bold text-green-700">
                          <MyPiggyIcon name="goal-completed" size="sm" />
                          Goal reached!
                        </p>
                        <p className="text-sm text-green-700">Complete it when you use the money.</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      {!fullyFunded && (
                        <Button
                          variant="kid"
                          onClick={() => openDeposit(goal)}
                          disabled={
                            balance <= 0 ||
                            loading ||
                            isWriteLocked
                          }
                        >
                          Add Money
                        </Button>
                      )}

                      {fullyFunded && (
                        <Button
                          variant="kid"
                          onClick={() => handleCompleteGoal(goal)}
                          disabled={
                            loading ||
                            isWriteLocked
                          }
                        >
                          <MyPiggyIcon name="goal-completed" size="sm" className="mr-2" />
                          Complete
                        </Button>
                      )}

                      <Button
                        variant="secondary"
                        onClick={() => handleCancelGoal(goal)}
                        disabled={
                          loading ||
                          isWriteLocked
                        }
                      >
                        Cancel Goal
                      </Button>

                      {savedAmount > 0 && eligibleTransferTargets.length > 0 && (
                        <Button
                          variant="secondary"
                          className="col-span-2"
                          onClick={() => openTransfer(goal)}
                          disabled={
                            loading ||
                            isWriteLocked
                          }
                        >
                          Transfer to Another Goal
                        </Button>
                      )}
                    </div>
                  </Card>

                  {isDepositOpen && (
                    <Card className="p-4 border-2 border-purple-200">
                      <h3 className="font-extrabold text-lg mb-1">Add Money to {goal.name}</h3>
                      <p className="text-sm font-bold text-slate-600 mb-4">
                        Still needed: {formatCurrency(Math.max(
                          Number(goal.target_amount_cents) - getGoalBalance(goal.id),
                          0
                        ))}
                      </p>
                      <form onSubmit={handleAddMoney} className="space-y-4">
                        <Input
                          label="How much to add?"
                          type="number"
                          value={addAmount}
                          onChange={(e) => setAddAmount(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0.01"
                          required
                        />
                        <p className="text-sm font-bold text-slate-600">
                          Available Pocket Money: {formatCurrency(balance)}
                        </p>
                        <div className="flex space-x-3">
                          <Button
                            type="button"
                            variant="secondary"
                            className="flex-1"
                            onClick={() => { setDepositGoal(null); setAddAmount(''); setError(''); }}
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            variant="kid"
                            className="flex-1"
                            disabled={
                              loading ||
                              isWriteLocked
                            }
                          >
                            {loading ? 'Adding...' : 'Add to Goal'}
                          </Button>
                        </div>
                      </form>
                    </Card>
                  )}

                  {isTransferOpen && (
                    <Card className="p-4 border-2 border-purple-200">
                      <h3 className="font-extrabold text-lg mb-2">Transfer Savings from {goal.name}</h3>
                      <p className="text-sm font-bold text-slate-600 mb-4">
                        You have {formatCurrency(savedAmount)} available to transfer.
                      </p>
                      <form onSubmit={handleTransfer} className="space-y-4">
                        <Input
                          label="How much do you want to transfer?"
                          type="number"
                          value={transferAmount}
                          onChange={(e) => setTransferAmount(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0.01"
                          max={(savedAmount / 100).toFixed(2)}
                          required
                        />

                        <label className="block">
                          <span className="block text-sm font-medium text-gray-700 mb-1">Transfer to</span>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-3"
                            value={transferTargetId}
                            onChange={(e) => setTransferTargetId(e.target.value)}
                            required
                          >
                            {eligibleTransferTargets.map((candidate) => {
                              const room = Math.max(
                                Number(candidate.target_amount_cents) - getGoalBalance(candidate.id),
                                0
                              );
                              return (
                                <option key={candidate.id} value={candidate.id}>
                                  {candidate.name} — {formatCurrency(getGoalBalance(candidate.id))} saved, {formatCurrency(room)} room
                                </option>
                              );
                            })}
                          </select>
                        </label>

                        {selectedTransferTarget && (
                          <p className="text-sm font-bold text-slate-600">
                            {selectedTransferTarget.name} can receive up to {formatCurrency(selectedTargetRoom)}.
                          </p>
                        )}

                        <div className="flex space-x-3">
                          <Button
                            type="button"
                            variant="secondary"
                            className="flex-1"
                            onClick={() => {
                              setTransferGoal(null);
                              setTransferTargetId('');
                              setTransferAmount('');
                              setError('');
                            }}
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            variant="kid"
                            className="flex-1"
                            disabled={loading || !transferTargetId || isWriteLocked}
                          >
                            {loading ? 'Transferring...' : 'Transfer'}
                          </Button>
                        </div>
                      </form>
                    </Card>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {activeGoals.length >= MAX_ACTIVE_GOALS && (
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <p className="text-sm font-bold text-yellow-800 text-center">
              You have 3 active goals. Complete or cancel one before creating another.
            </p>
          </Card>
        )}



      </div>
    </KidLayout>
  );
};

export default Savings;

